import { BetSpread, KellyConfig } from '../types';

// Spread betting strategies for card counting

// Default bet spreads for different strategies
export const DEFAULT_BET_SPREADS: Record<'conservative' | 'moderate' | 'aggressive', BetSpread[]> = {
  conservative: [
    { trueCount: -Infinity, betUnits: 1 },
    { trueCount: 2, betUnits: 2 },
    { trueCount: 3, betUnits: 3 },
    { trueCount: 4, betUnits: 4 },
    { trueCount: 5, betUnits: 5 }
  ],
  moderate: [
    { trueCount: -Infinity, betUnits: 1 },
    { trueCount: 2, betUnits: 2 },
    { trueCount: 3, betUnits: 4 },
    { trueCount: 4, betUnits: 8 },
    { trueCount: 5, betUnits: 12 }
  ],
  aggressive: [
    { trueCount: -Infinity, betUnits: 1 },
    { trueCount: 2, betUnits: 2 },
    { trueCount: 3, betUnits: 4 },
    { trueCount: 4, betUnits: 8 },
    { trueCount: 5, betUnits: 12 },
    { trueCount: 6, betUnits: 16 },
    { trueCount: 7, betUnits: 20 }
  ]
};

/**
 * Calculate Kelly Criterion optimal bet
 * 
 * Kelly Formula: f* = (bp - q) / b
 * Where:
 *   f* = fraction of bankroll to bet
 *   b = odds received on the bet (net odds, e.g., 1 for even money)
 *   p = probability of winning
 *   q = probability of losing (1 - p)
 * 
 * For blackjack, we simplify using edge:
 *   f* = edge / variance
 *   
 * With typical blackjack variance of ~1.15, we use:
 *   f* = edge / 1.15
 */
export function calculateKellyBet(
  trueCount: number,
  bankroll: number,
  minBet: number,
  maxBet: number,
  config: KellyConfig
): number {
  const { kellyFraction, edgePerTrueCount, baseEdge } = config;
  
  // Calculate player edge at current true count
  // Player edge = -baseEdge + (TC * edgePerTC)
  const playerEdge = -baseEdge + (trueCount * edgePerTrueCount);
  
  // If no edge, bet minimum
  if (playerEdge <= 0) {
    return minBet;
  }
  
  // Kelly formula for blackjack (variance ~1.15)
  const blackjackVariance = 1.15;
  const kellyPercentage = (playerEdge / 100) / blackjackVariance;
  
  // Apply Kelly fraction (quarter, half, or full Kelly)
  const adjustedKelly = kellyPercentage * kellyFraction;
  
  // Calculate bet amount
  let betAmount = bankroll * adjustedKelly;
  
  // Round to nearest minBet unit
  betAmount = Math.round(betAmount / minBet) * minBet;
  
  // Clamp to min/max
  return Math.max(minBet, Math.min(maxBet, betAmount));
}

/**
 * Calculate bet using a fixed spread table
 */
export function calculateSpreadBet(
  trueCount: number,
  minBet: number,
  maxBet: number,
  betSpread: BetSpread[]
): number {
  // Sort spread by true count descending to find highest matching
  const sortedSpread = [...betSpread].sort((a, b) => b.trueCount - a.trueCount);
  
  for (const spread of sortedSpread) {
    if (trueCount >= spread.trueCount) {
      const bet = minBet * spread.betUnits;
      return Math.min(bet, maxBet);
    }
  }
  
  return minBet;
}

/**
 * Get optimal bet using either Kelly or spread betting
 */
export function getOptimalBet(
  trueCount: number,
  minBet: number,
  maxBet: number,
  bankroll: number,
  strategy: 'conservative' | 'moderate' | 'aggressive' | 'kelly' = 'moderate',
  houseEdge: number = 0.5, // Default house edge
  kellyFraction: number = 0.5 // Default to half Kelly
): number {
  if (strategy === 'kelly') {
    return calculateKellyBet(trueCount, bankroll, minBet, maxBet, {
      kellyFraction,
      edgePerTrueCount: 0.5,
      baseEdge: houseEdge
    });
  }
  
  return calculateSpreadBet(trueCount, minBet, maxBet, DEFAULT_BET_SPREADS[strategy]);
}

/**
 * Get optimal bet with custom spread
 */
export function getOptimalBetWithSpread(
  trueCount: number,
  minBet: number,
  maxBet: number,
  betSpread: BetSpread[]
): number {
  return calculateSpreadBet(trueCount, minBet, maxBet, betSpread);
}

export function evaluateBetSizing(
  actualBet: number,
  optimalBet: number,
  _minBet: number // Reserved for future relative evaluation
): {
  accurate: boolean;
  percentageOff: number;
  rating: 'perfect' | 'good' | 'acceptable' | 'poor';
} {
  if (actualBet === optimalBet) {
    return { accurate: true, percentageOff: 0, rating: 'perfect' };
  }

  if (optimalBet === 0) {
    // Optimal is "don't bet" but the player bet something — fully off.
    return { accurate: false, percentageOff: 100, rating: 'poor' };
  }

  const difference = Math.abs(actualBet - optimalBet);
  const percentageOff = (difference / optimalBet) * 100;
  
  let rating: 'perfect' | 'good' | 'acceptable' | 'poor';
  
  if (percentageOff === 0) rating = 'perfect';
  else if (percentageOff <= 15) rating = 'good';
  else if (percentageOff <= 30) rating = 'acceptable';
  else rating = 'poor';
  
  return {
    accurate: percentageOff <= 15,
    percentageOff,
    rating
  };
}
